import { ReloadOutlined } from "@ant-design/icons";
import { Button, Tooltip, Upload, UploadFile, Image, Modal, Progress } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { PageHeader } from "../components/PageHeader";
import { TableSkeleton } from "../components/TableSkeleton";
import { RootState } from "../app/store";
import { clearErrorImages, fetchImages, showImageError } from "../features/imagesLibrarySlice";
import { deleteLibraryImage, imageUploadUrl } from "../features/server";
import { authHeader } from "../authConfig/token";
import ImgCrop from 'antd-img-crop';
import { Problem } from "../model/problem";
import { getErrorMessage } from "../utils/getErrorMessage";
import { REACT_APP_MAX_UPLOAD_SIZE } from "../config";
import { bytesToSize } from "../utils/sizeUtis";

export const ImagesLibraryPage = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const images = useAppSelector((state: RootState) => state.imageLibrary.images);
    const status = useAppSelector((state: RootState) => state.imageLibrary.status);
    const error = useAppSelector((state: RootState) => state.imageLibrary.error);
    const loaded = useAppSelector((state: RootState) => state.imageLibrary.loaded);
    const total = useAppSelector((state: RootState) => state.imageLibrary.total);

    const [previewVisible, setPreviewVisible] = useState(true);
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    useEffect(() => {
        dispatch(fetchImages());
    }, []);

    // Images stream in one-by-one from the store; keep the (controlled) upload
    // list in sync as each one arrives.
    useEffect(() => {
        setFileList(images?.map((image) => ({
            uid: image.image.id,
            name: image.image.name,
            status: 'done',
            url: image.url,
        } as UploadFile)) ?? []);
    }, [images]);

    const isLoadingContent = total > 0 && loaded < total;
    const progressPercent = total > 0 ? Math.round((loaded / total) * 100) : 0;

    const onReload = () => {
        dispatch(fetchImages());
    }

    const onDeleteFile = async (imageId: string) => {
        await deleteLibraryImage(imageId);
    }


    const errorMessage = getErrorMessage(error);


    const onBeforeUpload = (file: File) => {
       if(file.size > REACT_APP_MAX_UPLOAD_SIZE) {
          dispatch(showImageError({
              type: 'file-is-too-big',
              title: t('error.fileTooBig.title'),
              detail: t('error.fileTooBig.detail', {
                  name: file.name,
                  limit: bytesToSize(REACT_APP_MAX_UPLOAD_SIZE)
              })
          } as Problem));
          return false;
       }
       return file;
    }

    const header = (
        <PageHeader
            title={t("menu.imagesLibrary")}
            count={images?.length}
            actions={
                <Tooltip title={t("actions.refresh")}>
                    <Button
                        type="text"
                        shape="circle"
                        aria-label={t("actions.refresh")}
                        icon={<ReloadOutlined />}
                        onClick={onReload}
                    />
                </Tooltip>
            }
        />
    );

    if (status === 'idle' || status === 'loading') {
        return <>{header}<TableSkeleton rows={4} /></>;
    }


    return (
        <>
            {header}

            {isLoadingContent && (
                <Progress
                    className="mb-xs"
                    percent={progressPercent}
                    status="active"
                    format={() => `${loaded}/${total}`}
                />
            )}

            <div className="card media-card">
                <ImgCrop>
                    <Upload
                        accept=".jpg, .jpeg, .png"
                        fileList={fileList}
                        name="file"
                        action={imageUploadUrl}
                        listType="picture-card"
                        headers={authHeader()}
                        onChange={(info) => {
                            if (info.file.size != null && info.file.size > REACT_APP_MAX_UPLOAD_SIZE) {
                                info.fileList.pop();
                                setFileList([...info.fileList]);
                                return;
                            }
                            if (info.file.status === 'done') {
                                info.file.uid = info.file.response.id;
                            }
                            setFileList([...info.fileList]);
                        }}
                        onPreview={(file) => {
                            setPreviewVisible(true);
                            setPreviewImageUrl(file.url!);
                        }}
                        beforeUpload={onBeforeUpload}
                        withCredentials={true}
                        showUploadList={{ showPreviewIcon: true }}
                        onRemove={(file) => {
                            onDeleteFile((file.uid));
                        }}
                    >
                        <Button type="primary">{t("actions.upload")}</Button>
                    </Upload>
                </ImgCrop>
            </div>

            <Image src={previewImageUrl} preview={{
                visible: previewVisible, onVisibleChange: (value) => {
                    setPreviewVisible(value);
                }
            }} width={0} />
            <Modal title={errorMessage.title} visible={status === 'error' || status === 'auth-error'} onOk={() => dispatch(clearErrorImages())} cancelButtonProps={{ hidden: true }}>
                <p>{errorMessage.details}</p>
            </Modal>
        </>
    );
}
